<script lang="ts">
import { computed, ref } from 'vue';

import {
  PhxsgVideoPlayer as PhxsgCoreVideoPlayer,
  type PhxsgVideoPlayerProps as CoreProps,
  type VideoSource,
  type VideoCaption,
  type VideoFit,
} from '@sg-core/src/View/Components/Molecules/VideoPlayer';
import { useNormalizedClass } from '@sg-core/src/View/Components/composables';

import { useVideoPlayerControlsHtml } from './use-video-player-controls';

export type { VideoSource, VideoCaption, VideoFit };

export interface PhxsgVideoPlayerProps extends Omit<CoreProps, 'controlsHtml' | 'rootClass'> {
  playLabel?: string;
  pauseLabel?: string;
  muteLabel?: string;
  unmuteLabel?: string;
  rootClass?: string | null;
}
</script>

<script setup lang="ts">
defineOptions({ inheritAttrs: false });

const {
  playLabel = 'Play',
  pauseLabel = 'Pause',
  muteLabel = 'Mute',
  unmuteLabel = 'Unmute',
  rootClass,
  plyrOptions,
  ...coreProps
} = defineProps<PhxsgVideoPlayerProps>();

const coreVideoPlayerRef = ref<InstanceType<typeof PhxsgCoreVideoPlayer> | null>(null);
const playerElement = computed(() => coreVideoPlayerRef.value?.playerElement ?? null);
const player = computed(() => coreVideoPlayerRef.value?.player ?? null);

const computedRootClass = useNormalizedClass('phxsg-theme-blue-80', rootClass);
const controlsHtml = useVideoPlayerControlsHtml({ playLabel, pauseLabel, muteLabel, unmuteLabel });

const mergedPlyrOptions = computed(() => ({
  ...plyrOptions,
  controls: controlsHtml,
}));

defineExpose({ playerElement, player });
</script>

<template>
  <PhxsgCoreVideoPlayer
    ref="coreVideoPlayerRef"
    v-bind="{ ...coreProps, ...$attrs }"
    :root-class="computedRootClass"
    :plyr-options="mergedPlyrOptions"
  >
    <slot />
  </PhxsgCoreVideoPlayer>
</template>

<style src="./video-player.css" />
